import { z } from 'zod';

/**
 * Registration Validation Schemas
 *
 * Zod schemas for ELEV and PROFESOR registration forms
 * Used with react-hook-form + shadcn Form components
 */

/** Clasa lipsa intr-un camp de TEXT liber (formularul elevului cu cod) — nu un selector. */
export const MESAJ_CLASA_LIPSA_CAMP_LIBER = 'Completează clasa, de exemplu 5A';

// Base schema fields shared by both roles
const baseRegistrationSchema = z.object({
  email: z
    .string()
    /* `trim()` INAINTE de `.email()`: altfel un spatiu la capat — exact ce adauga
       autocompletarea pe telefon — face un e-mail valid sa fie respins cu «Email
       invalid», si omul n-are cum sa vada de ce. Ordinea inversa a fost aici de la
       inceput, pe toate cele cinci scheme; gasita 2026-09-18 de testul schemei de elev. */
    .transform((val) => val.trim().toLowerCase())
    .pipe(
      z.string()
        .min(1, { message: 'Email-ul este obligatoriu' })
        .email({ message: 'Email invalid' })
    ),
  parola: z
    .string()
    .transform((val) => val.trim())
    .pipe(
      z.string()
        .min(1, { message: 'Parola este obligatorie' })
        .min(6, { message: 'Parola trebuie să aibă minimum 6 caractere' })
    ),
  confirmaParola: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().min(1, { message: 'Confirmarea parolei este obligatorie' })),
});

// ELEV (Student) registration schema
export const elevRegistrationSchema = baseRegistrationSchema
  .extend({
    clasa: z
      .string()
      .transform((val) => val.trim())
      .pipe(z.string().min(1, { message: 'Selectați clasa în care sunteți' })),
    codProfesor: z
      .string()
      .transform((val) => val.trim())
      .pipe(z.string().min(1, { message: 'Codul profesorului este obligatoriu' })),
    nume: z
      .string()
      .transform((val) => val.trim())
      .pipe(
        z.string()
          .min(3, { message: 'Nickname-ul trebuie să aibă minimum 3 caractere' })
          .max(50, { message: 'Nickname-ul este prea lung' })
      ),
  })
  .refine((data) => data.parola === data.confirmaParola, {
    message: 'Parolele nu coincid',
    path: ['confirmaParola'],
  });

// PROFESOR (Teacher) registration schema
export const profesorRegistrationSchema = baseRegistrationSchema
  .extend({
    nume: z
      .string()
      .transform((val) => val.trim())
      .pipe(
        z.string()
          .min(3, { message: 'Numele și prenumele sunt obligatorii' })
          .max(100, { message: 'Numele este prea lung' })
      ),
    telefon: z
      .string()
      .transform((val) => val.trim())
      .pipe(
        z.string()
          .min(1, { message: 'Numărul de telefon este obligatoriu' })
          .min(10, { message: 'Numărul de telefon trebuie să aibă minimum 10 cifre' })
          .regex(/^[0-9+\s()-]+$/, { message: 'Număr de telefon invalid' })
      ),
  })
  .refine((data) => data.parola === data.confirmaParola, {
    message: 'Parolele nu coincid',
    path: ['confirmaParola'],
  });

// Forgot Password schema
export const forgotPasswordSchema = z.object({
  email: z
    .string()
    /* `trim()` INAINTE de `.email()`: altfel un spatiu la capat — exact ce adauga
       autocompletarea pe telefon — face un e-mail valid sa fie respins cu «Email
       invalid», si omul n-are cum sa vada de ce. Ordinea inversa a fost aici de la
       inceput, pe toate cele cinci scheme; gasita 2026-09-18 de testul schemei de elev. */
    .transform((val) => val.trim().toLowerCase())
    .pipe(
      z.string()
        .min(1, { message: 'Email-ul este obligatoriu' })
        .email({ message: 'Email invalid' })
    ),
});

/**
 * Login Schema
 *
 * Simple validation for login form:
 * - Email: Required, valid format, auto-lowercase on submit
 * - Password: Required, non-empty (no minimum length for backwards compatibility)
 *
 * Note: Password minimum length not enforced on login to support legacy accounts
 * that may have been created with different password rules.
 */
export const loginSchema = z.object({
  email: z
    .string()
    /* `trim()` INAINTE de `.email()`: altfel un spatiu la capat — exact ce adauga
       autocompletarea pe telefon — face un e-mail valid sa fie respins cu «Email
       invalid», si omul n-are cum sa vada de ce. Ordinea inversa a fost aici de la
       inceput, pe toate cele cinci scheme; gasita 2026-09-18 de testul schemei de elev. */
    .transform((val) => val.trim().toLowerCase())
    .pipe(
      z.string()
        .min(1, { message: 'Email este obligatoriu' })
        .email({ message: 'Email invalid' })
    ),
  parola: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().min(1, { message: 'Parola este obligatorie' })),
});

/**
 * Pupil Re-enrollment Schema
 *
 * Reinscrierea unui elev care avea cont in editia anterioara. Emailul si parola sunt cele
 * vechi; singurul lucru nou e clasa. Checkbox-ul de confirmare e obligatoriu.
 */
export const pupilReenrollSchema = z.object({
  email: z
    .string()
    /* `trim()` INAINTE de `.email()`: altfel un spatiu la capat — exact ce adauga
       autocompletarea pe telefon — face un e-mail valid sa fie respins cu «Email
       invalid», si omul n-are cum sa vada de ce. Ordinea inversa a fost aici de la
       inceput, pe toate cele cinci scheme; gasita 2026-09-18 de testul schemei de elev. */
    .transform((val) => val.trim().toLowerCase())
    .pipe(
      z.string()
        .min(1, { message: 'Email este obligatoriu' })
        .email({ message: 'Email invalid' })
    ),
  parola: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().min(1, { message: 'Parola este obligatorie' })),
  clasa: z.string().min(1, { message: 'Alege clasa din anul acesta' }),
  continuiInEditiaCurenta: z.boolean().refine((val) => val === true, {
    message: 'Confirmă că vrei să participi în ediția curentă',
  }),
});

export type PupilReenrollFormData = z.infer<typeof pupilReenrollSchema>;

/**
 * New Password Schema
 *
 * For password reset completion form:
 * - New password: Required, minimum 6 characters
 * - Confirm password: Required, must match new password
 *
 * Note: Token comes from URL params, not form fields
 */
export const newPasswordSchema = z
  .object({
    parola: z
      .string()
      .min(1, { message: 'Parola este obligatorie' })
      .min(6, { message: 'Parola trebuie să aibă minimum 6 caractere' }),
    confirmaParola: z
      .string()
      .min(1, { message: 'Confirmarea parolei este obligatorie' }),
  })
  .refine((data) => data.parola === data.confirmaParola, {
    message: 'Parolele nu coincid',
    path: ['confirmaParola'],
  });

/**
 * Wizardul de inscriere OTP-first (p8): Nume + Prenume (doua campuri, nu unul
 * singur ca la `profesorRegistrationSchema`) + Email + Telefon. Fara parola.
 */
export const profesorCodSchema = z.object({
  nume: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().min(1, { message: 'Numele este obligatoriu' }).max(50, { message: 'Numele este prea lung' })),
  prenume: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().min(1, { message: 'Prenumele este obligatoriu' }).max(50, { message: 'Prenumele este prea lung' })),
  email: z
    .string()
    /* `trim()` INAINTE de `.email()`: altfel un spatiu la capat — exact ce adauga
       autocompletarea pe telefon — face un e-mail valid sa fie respins cu «Email
       invalid», si omul n-are cum sa vada de ce. Ordinea inversa a fost aici de la
       inceput, pe toate cele cinci scheme; gasita 2026-09-18 de testul schemei de elev. */
    .transform((val) => val.trim().toLowerCase())
    .pipe(
      z.string()
        .min(1, { message: 'Email-ul este obligatoriu' })
        .email({ message: 'Email invalid' })
    ),
  telefon: z
    .string()
    .transform((val) => val.trim())
    .pipe(
      z.string()
        .min(1, { message: 'Numărul de telefon este obligatoriu' })
        .min(10, { message: 'Numărul de telefon trebuie să aibă minimum 10 cifre' })
        .regex(/^[0-9+\s()-]+$/, { message: 'Număr de telefon invalid' })
    ),
});

/**
 * Inscrierea ELEVULUI prin cod, fara parola (V1c, 21.09).
 *
 * 🔴 Schema PROPRIE, nu o extindere a lui `profesorCodSchema`: acolo telefonul e obligatoriu
 * (`:179-187`), iar aici e optional prin decizia owner-ului din 18.09. O extindere ar fi cerut
 * suprascrierea campului, adica exact locul unde o regula se schimba fara sa se vada in diff.
 *
 * ⚠️ Telefonul optional se scrie `.optional()` PESTE validarea de forma, nu prin slabirea ei: un
 * numar SCRIS trebuie sa arate in continuare a numar. Gol inseamna „nu l-a dat", nu „orice".
 *
 * ⚠️ `codProfesor` NU se valideaza pe lungime sau pe forma aici. Verdictul e al backendului, care
 * stie si codurile de profesor, si codurile SE ale scolilor; o regula de forma ghicita in frontend
 * ar respinge coduri valide inainte sa plece cererea.
 */
export const elevCodSchema = z.object({
  nume: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().min(1, { message: 'Numele este obligatoriu' }).max(50, { message: 'Numele este prea lung' })),
  prenume: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().min(1, { message: 'Prenumele este obligatoriu' }).max(50, { message: 'Prenumele este prea lung' })),
  /**
   * 🔴 `trim()` INAINTE de `.email()`: un spatiu la capat — exact ce adauga autocompletarea pe
   * telefon — nu mai face un e-mail valid sa fie respins cu «Email invalid». Gasit de testul
   * schemei asteia, 2026-09-18; aceeasi ordine e acum si in celelalte patru scheme
   * (`fix/l1-email-trim`, `email-trim.test.ts`).
   */
  email: z
    .string()
    .transform((val) => val.trim().toLowerCase())
    .pipe(
      z.string()
        .min(1, { message: 'Email-ul este obligatoriu' })
        .email({ message: 'Email invalid' })
    ),
  telefon: z
    .string()
    .transform((val) => val.trim())
    .pipe(
      z.string()
        .min(10, { message: 'Numărul de telefon trebuie să aibă minimum 10 cifre' })
        .regex(/^[0-9+\s()-]+$/, { message: 'Număr de telefon invalid' })
        .optional()
    )
    .optional()
    /* Gol = necompletat, nu invalid: campul e optional, iar `''` n-are voie sa cada pe `min(10)`. */
    .or(z.literal('')),
  clasa: z
    .string()
    .transform((val) => val.trim())
    /* Camp text liber («ex. 5A»), NU un `Select` cu lista: mesajul «Selectați clasa…» de la
       schemele cu parola promite un selector care aici nu exista (V1, 2026-09-18). */
    .pipe(z.string().min(1, { message: MESAJ_CLASA_LIPSA_CAMP_LIBER })),
  codProfesor: z
    .string()
    .transform((val) => val.trim())
    .pipe(z.string().min(1, { message: 'Codul profesorului este obligatoriu' })),
});

// TypeScript types inferred from schemas
export type ElevRegistrationFormData = z.infer<typeof elevRegistrationSchema>;
export type ProfesorRegistrationFormData = z.infer<typeof profesorRegistrationSchema>;
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type NewPasswordFormData = z.infer<typeof newPasswordSchema>;
export type ProfesorCodFormData = z.infer<typeof profesorCodSchema>;
export type ElevCodFormData = z.infer<typeof elevCodSchema>;
